import type { TicketInformationResponse } from '@/pages/api/[eventId]/ticket-information.page';
import { useBasicInfo } from '@/components/_shared/contexts/BasicInfoContext';
import {
  updateWaitlistPageProperty,
  trackConversionBarHydration,
  updateTicketInformationPageProperties,
} from '@/utils/dataLayer';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

export const TICKET_INFORMATION_TIMEOUT_MS = 10000;

/**
 * Module-level flag to ensure dataLayer page properties and the hydration event
 * are only pushed once per page load, regardless of how many components call
 * useTicketInformation.
 */
let hasTrackedPageProperties = false;

/** @internal Exposed for testing only */
export function _resetTracking() {
  hasTrackedPageProperties = false;
}

/**
 * Fetches ticket information for a given event from the API.
 *
 * This function implements a timeout mechanism to prevent long-running requests.
 * If the request exceeds the timeout duration or the API call fails, it returns null.
 *
 * @param eventId
 * @param organizationId
 * @param isSeries - Whether the event is part of a series
 * @param isParentEvent - Whether the event is a parent event in a series
 * @param isOnline - Whether the event is online (affects caching and client IP forwarding)
 * @returns A Promise that resolves to:
 *   - `TicketInformationResponse` if the API call succeeds within the timeout
 *   - `null` if the request times out or the API call fails
 */
export const getTicketInformation = async (
  eventId: string,
  organizationId: string,
  isSeries: boolean,
  isParentEvent: boolean,
  isOnline: boolean,
  requestedPromoCode?: string,
  eventTimezone?: string,
): Promise<TicketInformationResponse | null> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, TICKET_INFORMATION_TIMEOUT_MS);

  const isChild = isSeries && !isParentEvent;
  const params = new URLSearchParams({
    organizationId,
    isChild: String(isChild),
    isOnline: String(isOnline),
  });
  if (requestedPromoCode) {
    params.set('discountCode', requestedPromoCode);
  }
  if (eventTimezone) {
    params.set('eventTimezone', eventTimezone);
  }

  try {
    const response = await fetch(`/e/api/${eventId}/ticket-information?${params.toString()}`, {
      signal: controller.signal,
    });
    if (!response.ok) {
      return null;
    }
    return response.json();
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      console.error('Ticket information request timed out and was aborted.');
      return null;
    }
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
};

export const useTicketInformation = (): [TicketInformationResponse | null, boolean] => {
  const { id: eventId, organizationId, isSeries, isParentEvent, isOnline, startDate } = useBasicInfo();
  const searchParams = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const requestedPromoCode = searchParams.get('discount') || searchParams.get('access') || undefined;
  const eventTimezone = startDate?.timezone;
  const { data, isLoading } = useQuery({
    queryKey: [
      'ticketInformation',
      eventId,
      organizationId,
      isSeries,
      isParentEvent,
      isOnline,
      eventTimezone,
      requestedPromoCode,
    ],
    queryFn: () =>
      getTicketInformation(
        eventId,
        organizationId,
        isSeries ?? false,
        isParentEvent,
        isOnline ?? false,
        requestedPromoCode,
        eventTimezone,
      ),
  });

  useEffect(() => {
    if (data?.availability && !hasTrackedPageProperties) {
      hasTrackedPageProperties = true;
      const hasWaitlistEnabled = !!data.availability.waitlistAvailable || !!data.availability.waitlistEnabled;
      updateWaitlistPageProperty(hasWaitlistEnabled);
      updateTicketInformationPageProperties(data);
      trackConversionBarHydration();
    }
  }, [data]);

  return [data ?? null, isLoading];
};
